import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, ArrowDownRight, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { formatCurrency, formatDate } from '../../utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Deposits() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeposits();
  }, []);

  const fetchDeposits = async () => {
    try {
      const response = await axios.get(`${API_URL}/deposits`);
      setDeposits(response.data);
    } catch (error) {
      // Error silenced: 'Failed to fetch deposits:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-500/10 text-amber-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {t('deposits.awaitingApproval')}
          </span>
        );
      case 'pending_payment':
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-500/10 text-slate-400 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {t('deposits.paymentNotCompleted')}
          </span>
        );
      case 'approved':
        return (
          <span className="badge-approved flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            {t('common.approved')}
          </span>
        );
      case 'rejected':
        return (
          <span className="badge-rejected flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            {t('common.rejected')}
          </span>
        );
      default:
        return <span className="text-slate-400">{status}</span>;
    }
  };

  const getPaymentMethod = (method) => {
    switch (method) {
      case 'mbway':
        return 'MB Way';
      case 'card':
        return t('deposits.card');
      case 'transferencia':
        return t('deposits.transfer');
      default:
        return method;
    }
  };

  return (
    <div className="animate-fade-in" data-testid="deposits-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">{t('deposits.myDeposits')}</h1>
          <p className="text-slate-400 text-sm">{t('deposits.historySubtitle')}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : deposits.length > 0 ? (
        <div className="glass-card overflow-hidden">
          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr className="bg-white/5">
                  <th>{t('common.date')}</th>
                  <th>{t('common.amount')}</th>
                  <th>{t('common.method')}</th>
                  <th>{t('common.status')}</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map((deposit) => (
                  <tr key={deposit.id}>
                    <td className="text-slate-300">
                      {new Date(deposit.created_at).toLocaleDateString('pt-PT', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="text-white font-semibold">
                      {formatCurrency(deposit.amount)}
                    </td>
                    <td className="text-slate-300">
                      {getPaymentMethod(deposit.payment_method)}
                    </td>
                    <td>{getStatusBadge(deposit.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden divide-y divide-white/5">
            {deposits.map((deposit) => (
              <div key={deposit.id} className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <ArrowDownRight className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">{formatCurrency(deposit.amount)}</p>
                      <p className="text-sm text-slate-400">{getPaymentMethod(deposit.payment_method)}</p>
                    </div>
                  </div>
                  {getStatusBadge(deposit.status)}
                </div>
                <p className="text-xs text-slate-500">
                  {new Date(deposit.created_at).toLocaleDateString('pt-PT', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
            <ArrowDownRight className="w-8 h-8 text-slate-500" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">{t('deposits.noDeposits')}</h3>
          <p className="text-slate-400">{t('deposits.noDepositsYet')}</p>
        </div>
      )}
    </div>
  );
}
